import { TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { useDadosEmpresa, useAtualizarDadosEmpresa } from '@/services/Configuracoes';

function Campo({ label, value }: { label: string; value?: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="text-sm font-medium text-foreground bg-muted/40 rounded-md px-3 py-2 border border-border/50">
        {value || '—'}
      </p>
    </div>
  );
}

function CampoSkeleton() {
  return (
    <div className="space-y-1">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-9 w-full" />
    </div>
  );
}

export function DadosEmpresaTab() {
  const { data: response, isLoading } = useDadosEmpresa();
  const { mutate: atualizar, isPending } = useAtualizarDadosEmpresa();
  const empresa = response?.data;

  return (
    <TabsContent value="dados-empresa" className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Dados Cadastrais</CardTitle>
            {empresa?.DataAtualizacaoCadastral && (
              <p className="text-xs text-muted-foreground mt-1">
                Última atualização: {empresa.DataAtualizacaoCadastral}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={isPending || isLoading}
            onClick={() => atualizar()}
          >
            <RefreshCw className={`h-4 w-4 ${isPending ? 'animate-spin' : ''}`} />
            Atualizar Dados Cadastrais
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <CampoSkeleton />
                <CampoSkeleton />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <CampoSkeleton />
                <CampoSkeleton />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <CampoSkeleton />
                <CampoSkeleton />
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Campo label="Razão Social" value={empresa?.RazaoSocial} />
                <Campo label="Nome Fantasia" value={empresa?.NomeFantasia} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Campo label="CNPJ" value={empresa?.CNPJ} />
                <Campo label="Inscrição Estadual" value={empresa?.InscricaoEstadual} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Campo label="Regime Tributário" value={empresa?.RegimeTributario} />
                <Campo label="CNAE Principal" value={empresa?.CnaePrincipal} />
              </div>

              <Separator />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                Endereço
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Campo label="Logradouro" value={empresa?.Logradouro} />
                <Campo label="Bairro" value={empresa?.Bairro} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Campo label="Cidade" value={empresa?.Cidade} />
                <Campo label="UF" value={empresa?.UF} />
                <Campo label="CEP" value={empresa?.CEP} />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}
